import React, { useEffect } from 'react';
import { 
  XMarkIcon, 
  ArrowTopRightOnSquareIcon, 
  DocumentIcon,
  ArrowDownTrayIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { resourcesService, Resource } from '@/services/resources';

interface ResourcePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
}

export const ResourcePreviewModal: React.FC<ResourcePreviewModalProps> = ({
  isOpen,
  onClose,
  resource
}) => {
  // Removed unused state variables

  useEffect(() => {
    if (isOpen && resource) {
      // Log the preview access
      resourcesService.logAccess(resource.id, 'preview').catch(console.warn);
    }
  }, [isOpen, resource]);

  const handleOpenExternal = async () => {
    if (!resource) return;

    try {
      await resourcesService.logAccess(resource.id, 'click');
      if (resource.resource_type === 'link' && resource.url) {
        window.open(resource.url, '_blank');
      }
    } catch (err) {
      console.error('Error opening external resource:', err);
    }
  };

  const handleDownload = async () => {
    if (!resource) return;

    try {
      await resourcesService.logAccess(resource.id, 'download');
      
      if (resource.resource_type === 'document' && resource.file_path) {
        const downloadUrl = `/api/v1/resources/${resource.id}/download`;
        
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = resource.file_name || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error downloading resource:', err);
    }
  };

  const handleShare = async () => {
    if (!resource) return;

    try {
      const shareUrl = window.location.origin + `/resources/${resource.id}`;
      const shareText = `${resource.title}${resource.description ? ` - ${resource.description}` : ''}`;
      
      if (navigator.share && navigator.canShare && navigator.canShare()) {
        await navigator.share({
          title: resource.title,
          text: shareText,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard');
      }
    } catch (err) {
      console.error('Error sharing resource:', err);
    }
  };

  if (!isOpen || !resource) return null;

  const isActive = resourcesService.isResourceActive(resource);
  const securityLevel = resourcesService.getSecurityLevel(resource);
  const securityColor = resourcesService.getSecurityLevelColor(securityLevel);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {resource.resource_type === 'link' ? (
                <ArrowTopRightOnSquareIcon className="h-6 w-6 text-blue-600" />
              ) : (
                <DocumentIcon className="h-6 w-6 text-green-600" />
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{resource.title}</h2>
                <p className="text-sm text-gray-500">
                  {resource.resource_type === 'link' ? 'External Link' : 'Document'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs ${securityColor} bg-opacity-10`}>
                {securityLevel}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs ${
                isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Description */}
            {resource.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                <p className="text-gray-600">{resource.description}</p>
              </div>
            )}

            {/* Resource Content */}
            <div className="mb-6">
              {resource.resource_type === 'link' && resource.url && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 text-blue-600" />
                      <span className="text-blue-600 font-medium">External Link</span>
                    </div>
                    <button
                      onClick={handleOpenExternal}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Open in New Tab
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 break-all">{resource.url}</p>
                  
                  {/* Embedded Preview for certain link types */}
                  {resource.url && (
                    <div className="mt-4">
                      <div className="bg-white border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Preview</span>
                          <button
                            onClick={handleOpenExternal}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Open in new tab →
                          </button>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p className="mb-2">🔗 {resource.url}</p>
                          <p className="text-xs text-gray-500">
                            Click &quot;Open in New Tab&quot; to view the full content
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {resource.resource_type === 'document' && resource.file_path && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <DocumentIcon className="h-5 w-5 text-green-600" />
                      <span className="text-green-600 font-medium">Document</span>
                    </div>
                    <button
                      onClick={handleDownload}
                      className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>Download</span>
                    </button>
                  </div>
                  
                  {resource.file_name && (
                    <p className="text-sm text-gray-600 mb-2">{resource.file_name}</p>
                  )}
                  
                  {resource.file_size && (
                    <p className="text-xs text-gray-500 mb-2">
                      Size: {(resource.file_size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                  
                  <p className="text-xs text-gray-500 break-all">
                    File: {resource.file_path}
                  </p>

                  {/* Document Preview */}
                  <div className="mt-4">
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Document Preview</span>
                        <button
                          onClick={handleDownload}
                          className="text-xs text-green-600 hover:text-green-700"
                        >
                          Download →
                        </button>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p className="mb-2">📄 {resource.file_name || 'Document'}</p>
                        <p className="text-xs text-gray-500">
                          Click &quot;Download&quot; to view the full document
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            {resource.tags && resource.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {resource.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag.name}
                      {tag.is_predefined && <span className="ml-1">★</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Access Count:</span>
                <span className="ml-2 text-gray-600">{resource.access_count || 0} views</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created:</span>
                <span className="ml-2 text-gray-600">
                  {new Date(resource.created_at).toLocaleDateString()}
                </span>
              </div>
              {resource.last_accessed && (
                <div>
                  <span className="font-medium text-gray-700">Last Accessed:</span>
                  <span className="ml-2 text-gray-600">
                    {new Date(resource.last_accessed).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-4">
              {resource.resource_type === 'link' && resource.url && (
                <button
                  onClick={handleOpenExternal}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  <span>Open Link</span>
                </button>
              )}
              
              {resource.resource_type === 'document' && resource.file_path && (
                <button
                  onClick={handleDownload}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Download</span>
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                <ShareIcon className="h-4 w-4" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
